import {User} from "firebase/app";
import {AngularFireAuth} from "@angular/fire/auth";
import {Router} from "@angular/router";
import {Account} from "../../models/account";
import {ReplaySubject} from "rxjs";
import {first, map, mergeMap, tap} from "rxjs/operators";
import {Utils} from "../../utils";
import {environment} from "@environments/environment";
import {Company} from "../../models/company";
import {RestApiManager} from "../rest-api-manager";
import {Role} from "../../models/role";
import {Injectable} from "@angular/core";
import {IAccountManagerService} from "./iaccount-manager-service";
import {DEBUG, DebugService} from "../debug.service";
import {BaseAccountManagerService} from "./base-account-manager-service";

@Injectable({
  providedIn: 'root'
})
export class FirebaseAccountManagerService extends BaseAccountManagerService implements IAccountManagerService {

  account: Account
  onLoginStatusChanged: ReplaySubject<boolean>;
  getRoles: (id: string) => Promise<Role[]>;
  isAdminOrExpert: () => boolean;

  private tokenAutoRefreshTimer: any;

  constructor(
    private auth: AngularFireAuth,
    private router: Router,
    private restApiManager: RestApiManager,
    private debugService: DebugService,
  ) { super(router); }

  async login(email: string, password: string): Promise<boolean> {

    let user: User;
    let authToken: string;

    try {

      user = (await this.auth.signInWithEmailAndPassword(email, password)).user;
      authToken = await user.getIdToken();

    } catch (e) {
      return false;
    }

    if (await this.postLogin(user, authToken)) {
      await this.router.navigate(['/']);
      return true;
    } else {
      return false;
    }

  }

  ssoLogin(accessToken: string, refreshToken: string): Promise<boolean> {
    throw new Error("SSO login is not implemented");
  }

  async loginAnonymous(firstName?: string, lastName?: string): Promise<boolean> {

    try {

      let user = await this.auth.authState.pipe(first()).toPromise();

      // If signed in as expert -> logout
      if (user !== null && !user.isAnonymous) {
        this.debugService.log(DEBUG.LOGIN, 'Signing out as expert...');
        await this.auth.signOut();
      }

      // Anonymous login if not logged in
      if (user === null) {
        this.debugService.log(DEBUG.LOGIN, 'Signing in as anonymous...');
        user = (await this.auth.signInAnonymously()).user;
      }

      this.account.authProviderId = user.uid;
      this.account.authToken = await user.getIdToken();
      this.account.firstName = firstName;
      this.account.lastName = lastName;

      this.onLoginStatusChanged.next(true);
      return true;

    } catch (e) { }

    this.onLoginStatusChanged.next(false);
    return false;

  }

  async loginWithCustomToken(token: string): Promise<boolean> {

    let user = await this.auth.authState.pipe(first()).toPromise();

    // If signed in as expert -> logout
    if (user !== null) {
      this.debugService.log(DEBUG.LOGIN, 'Signing out...');
      await this.auth.signOut();
    }

    try {

      const firebaseUser = (await this.auth.signInWithCustomToken(token)).user;
      const authToken = await firebaseUser.getIdToken();

      return await this.postLogin(firebaseUser, authToken);

    } catch (ex) {
      return false;
    }

  }

  async postLogin(user: User, authToken: string, userOnly: boolean = false): Promise<boolean> {

    if (Utils.isEmpty(user.uid) || Utils.isEmpty(authToken)) {
      return false;
    }

    this.account.authProviderId = user.uid;
    this.account.authToken = authToken;

    // Get user account
    const account = await this.restApiManager.get<Account>(
      Account,
      `${environment.restApi.endpoint}/users/uid/${this.account.authProviderId}`,
      this.account.authToken
    );

    // Set account properties
    this.account.id = account.id;
    this.account.firstName = account.firstName;
    this.account.lastName = account.lastName;
    this.account.isAnonymous = await this.isAnonymous();

    if (!userOnly) {

      // Get company
      this.account.company = await this.restApiManager.get<Company>(
        Company,
        `${environment.restApi.endpoint}/companies/${account.company.id}`,
        this.account.authToken
      );

      // Get roles
      this.account.roles = await this.getRoles(account.id);

      // Logout if not expert
      if (!this.isAdminOrExpert()) {
        return false;
      }

    }

    const accountResult = this.account.validate();

    if (accountResult && !this.tokenAutoRefreshTimer) {
      this.startTokenAutoRefresh(user);
    }

    this.onLoginStatusChanged.next(accountResult);

    return accountResult;

  }

  isLoggedIn(): Promise<boolean> {
    return this.auth.authState.pipe(
      first(),
      mergeMap(async firebaseUser => firebaseUser !== null && !firebaseUser.isAnonymous && await this.postLogin(firebaseUser, await firebaseUser.getIdToken())),
      tap(result => !result ? this.router.navigate(['login']) : null)
    ).toPromise();
  }

  isAnonymous(): Promise<boolean> {
    return this.auth.authState.pipe(
      first(),
      map(user => user !== null && user.isAnonymous)
    ).toPromise();
  }

  async requestPIN(): Promise<string> {
    return (await this.restApiManager.post<{ pin: string }>(
      `${environment.restApi.endpoint}/auth/pin`,
      { },
      this.account.authToken
    )).pin;
  }

  async logout() {
    this.goToLoginPage();
    this.account.clear();
    this.onLoginStatusChanged.next(false);
    await this.auth.signOut();
    clearInterval(this.tokenAutoRefreshTimer);
    this.tokenAutoRefreshTimer = null;
  }

  startTokenAutoRefresh(user: User): void {
    this.tokenAutoRefreshTimer = setInterval(async () => {
      this.account.authToken = await user.getIdToken(true);
    }, 10 * 60 * 1000);
  }

}
